import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  getMemberSessionAttendanceService,
  getMemberLocationsByDateService,
  get_all_registered_students,
  get_unknown_session_status,
  get_home_page_summary,
  get_suspect_report
} from "services/api-service";

/* ─── styles ──────────────────────────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --bg:        #f0f4f8;
  --bg2:       #e8edf5;
  --card:      #ffffff;
  --card2:     #f8fafc;
  --border:    rgba(0,0,0,0.07);
  --border2:   rgba(0,0,0,0.12);
  --text:      #0f172a;
  --text2:     #475569;
  --text3:     #94a3b8;
  --blue:      #2563eb;
  --blue2:     #1d4ed8;
  --green:     #059669;
  --red:       #dc2626;
  --amber:     #d97706;
  --purple:    #7c3aed;
  --cyan:      #0891b2;
  --font:      'Sora', sans-serif;
  --mono:      'JetBrains Mono', monospace;
}

.dash-root {
  background: var(--bg);
  min-height: 100vh;
  font-family: var(--font);
  color: var(--text);
  padding: 0 0 60px;
}

/* ── header ── */
.dash-header {
  background: linear-gradient(135deg, #0f172a 0%, #334155 100%);
  border-bottom: 1px solid rgba(255,255,255,0.15);
  padding: 22px 36px 20px;
  position: relative;
  overflow: hidden;
}
.dash-header::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: radial-gradient(ellipse 50% 100% at 90% 50%, rgba(255,255,255,0.06) 0%, transparent 70%);
  pointer-events: none;
}
.dash-header-inner {
  max-width: 1440px; margin: 0 auto;
  display: flex; align-items: center; justify-content: space-between;
  gap: 20px; flex-wrap: wrap;
}
.dash-brand { display: flex; align-items: center; gap: 14px; }
.dash-brand-icon {
  width: 46px; height: 46px;
  background: rgba(255,255,255,0.15);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 24px;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.2);
}
.dash-brand-text h1 {
  font-size: 20px; font-weight: 800; color: #fff;
  letter-spacing: -0.4px; line-height: 1.2;
}
.dash-brand-text p {
  font-size: 12px; color: rgba(255,255,255,0.65); font-weight: 400; margin-top: 2px;
  font-family: var(--mono);
}
.dash-date-pill {
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.25);
  padding: 8px 18px; border-radius: 8px;
  font-size: 13px; color: #fff; font-weight: 600;
  font-family: var(--mono); white-space: nowrap;
  backdrop-filter: blur(4px);
}

/* ── body ── */
.dash-body { max-width: 1440px; margin: 0 auto; padding: 28px 36px 0; }

/* ── location pills ── */
.dash-loc-bar {
  background: var(--card);
  border: 1px solid var(--border2);
  border-radius: 14px;
  padding: 14px 20px;
  margin-bottom: 24px;
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.dash-loc-label {
  font-size: 11px; color: var(--text3); font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.1em;
  font-family: var(--mono); white-space: nowrap;
}
.dash-loc-pills { display: flex; gap: 8px; flex-wrap: wrap; }
.dash-loc-pill {
  padding: 7px 16px; border-radius: 8px;
  font-size: 12px; font-weight: 600;
  cursor: pointer; border: 1.5px solid var(--border2);
  background: var(--card2); color: var(--text2);
  transition: all 0.18s; font-family: var(--font);
  white-space: nowrap;
}
.dash-loc-pill:hover { border-color: var(--blue); color: var(--blue); background: #eff6ff; }
.dash-loc-pill.active {
  background: var(--blue); border-color: var(--blue);
  color: #fff; box-shadow: 0 3px 10px rgba(37,99,235,0.3);
}

/* ── 6 stat cards ── */
.dash-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}
@media (max-width: 768px)  { .dash-stats-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px)  { .dash-stats-grid { grid-template-columns: 1fr; } }

.dash-stat {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 18px 16px 16px;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.dash-stat::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: var(--accent, var(--blue));
  border-radius: 14px 14px 0 0;
}
.dash-stat:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.13);
  border-color: var(--accent, var(--blue));
}
.dash-stat-icon-wrap {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; margin-bottom: 12px;
  background: var(--icon-bg, #eff6ff);
}
.dash-stat-label {
  font-size: 10px; font-weight: 700;
  color: var(--text3); text-transform: uppercase; letter-spacing: 0.1em;
  font-family: var(--mono); margin-bottom: 5px;
}
.dash-stat-value {
  font-size: 32px; font-weight: 800;
  color: var(--accent, var(--blue));
  font-family: var(--mono); letter-spacing: -1px;
  line-height: 1;
}
.dash-stat-sub {
  font-size: 11px; color: var(--text3); margin-top: 5px; font-weight: 500;
}
.dash-stat-click-hint {
  font-size: 10px; font-weight: 600;
  margin-top: 6px; font-family: var(--mono);
  display: flex; align-items: center; gap: 4px;
  color: var(--accent, var(--blue));
  opacity: 0.7;
}

/* ── section heading ── */
.dash-section-head {
  display: flex; align-items: center; gap: 12px; margin-bottom: 16px;
}
.dash-section-head h2 {
  font-size: 15px; font-weight: 700; color: var(--text); letter-spacing: -0.2px; white-space: nowrap;
}
.dash-section-head-line { flex: 1; height: 1px; background: var(--border2); }
.dash-section-badge {
  background: #eff6ff; color: var(--blue); font-size: 12px; font-weight: 700;
  padding: 3px 10px; border-radius: 6px; font-family: var(--mono); white-space: nowrap;
}

/* ── visitor lists grid ── */
.dash-lists-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 28px;
}
@media (max-width: 900px) { .dash-lists-grid { grid-template-columns: 1fr; } }

.dash-list-card {
  background: var(--card); border: 1px solid var(--border2); border-radius: 14px;
  overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.dash-list-head {
  display: flex; align-items: center; gap: 10px; padding: 16px 18px 14px;
  border-bottom: 1px solid var(--border); background: var(--card2);
}
.dash-list-head-icon {
  width: 34px; height: 34px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0;
}
.dash-list-head-icon.green { background: #dcfce7; }
.dash-list-head-icon.amber { background: #fef3c7; }
.dash-list-head-title { flex: 1; font-size: 14px; font-weight: 700; color: var(--text); }
.dash-list-count { font-size: 13px; font-weight: 800; padding: 3px 10px; border-radius: 6px; font-family: var(--mono); }
.dash-list-count.green { background: #dcfce7; color: #15803d; }
.dash-list-count.amber { background: #fef3c7; color: #92400e; }

.dash-visitor-list {
  padding: 10px; max-height: 420px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 7px;
}
.dash-visitor-list::-webkit-scrollbar { width: 4px; }
.dash-visitor-list::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 10px; }

.dash-visitor-row {
  display: flex; align-items: center; gap: 11px; padding: 11px 13px; border-radius: 9px;
  border: 1px solid var(--border); background: var(--card2); transition: all 0.15s ease;
}
.dash-visitor-row:hover { border-color: #bfdbfe; background: #eff6ff; transform: translateX(3px); }

.dash-rank { font-size: 18px; width: 26px; text-align: center; flex-shrink: 0; }
.dash-avatar {
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800; color: #fff; flex-shrink: 0;
  text-transform: uppercase; letter-spacing: 0.5px; font-family: var(--mono);
}
.dash-visitor-info { flex: 1; min-width: 0; }
.dash-visitor-name { font-size: 13px; font-weight: 700; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dash-visitor-meta { font-size: 11px; color: var(--text3); margin-top: 2px; font-family: var(--mono); }
.dash-visitor-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.dash-badge { font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 5px; font-family: var(--mono); white-space: nowrap; }
.dash-badge.green  { background: #dcfce7; color: #15803d; }
.dash-badge.amber  { background: #fef3c7; color: #92400e; }
.dash-badge.blue   { background: #dbeafe; color: #1d4ed8; }
.dash-badge.red    { background: #fee2e2; color: #991b1b; }

/* ── top inside time section ── */
.dash-visit-list { display: flex; flex-direction: column; gap: 9px; }
.dash-visit-row {
  display: flex; align-items: center; gap: 13px; padding: 13px 16px;
  background: var(--card); border: 1px solid var(--border2); border-radius: 11px;
  transition: all 0.15s; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.dash-visit-row:hover { border-color: #bfdbfe; transform: translateX(4px); box-shadow: 0 4px 12px rgba(37,99,235,0.08); }
.dash-visit-rank { font-size: 20px; width: 26px; text-align: center; flex-shrink: 0; }
.dash-visit-info { flex: 1; }
.dash-visit-name { font-size: 13px; font-weight: 700; color: var(--text); }
.dash-visit-bar-bg { height: 4px; background: #e2e8f0; border-radius: 10px; margin-top: 7px; }
.dash-visit-bar { height: 4px; border-radius: 10px; background: linear-gradient(90deg, var(--blue2), var(--cyan)); transition: width 0.5s ease; }
.dash-visit-hrs { font-family: var(--mono); font-size: 14px; font-weight: 800; color: var(--cyan); white-space: nowrap; }

.dash-section { margin-bottom: 28px; }

.dash-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 36px 20px; gap: 8px; color: var(--text3);
}
.dash-empty-icon { font-size: 36px; opacity: 0.35; }
.dash-empty-text { font-size: 14px; font-weight: 600; color: var(--text2); }
.dash-empty-sub  { font-size: 12px; color: var(--text3); }

.dash-spinner-row {
  display: flex; align-items: center; gap: 10px;
  color: var(--text3); font-size: 13px; padding: 18px 0; font-family: var(--mono);
}
.dash-spinner {
  width: 20px; height: 20px;
  border: 2px solid var(--border2); border-top-color: var(--blue);
  border-radius: 50%; animation: spin 0.7s linear infinite; flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ══════════════════════════════════════════════════
   UNIVERSAL MODAL STYLES
══════════════════════════════════════════════════ */
.uni-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(10, 15, 40, 0.72);
  backdrop-filter: blur(6px);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 30px 16px 30px; overflow-y: auto;
  animation: fadeOverlay 0.22s ease;
}
@keyframes fadeOverlay { from { opacity: 0; } to { opacity: 1; } }

.uni-modal {
  background: #fff; border-radius: 20px;
  width: 100%; max-width: 1000px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.28);
  overflow: hidden; flex-shrink: 0;
  animation: slideUp 0.26s cubic-bezier(0.22,1,0.36,1);
}
@keyframes slideUp { from { opacity:0; transform: translateY(36px); } to { opacity:1; transform: translateY(0); } }

.uni-modal-header {
  padding: 24px 28px 22px;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  position: relative; overflow: hidden;
}
.uni-modal-header::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(ellipse 60% 120% at 80% 50%, rgba(255,255,255,0.08) 0%, transparent 70%);
  pointer-events: none;
}
.uni-modal-title { display: flex; align-items: center; gap: 14px; }
.uni-modal-title-icon {
  width: 48px; height: 48px; border-radius: 13px;
  background: rgba(255,255,255,0.2); border: 1px solid rgba(255,255,255,0.25);
  display: flex; align-items: center; justify-content: center; font-size: 26px;
}
.uni-modal-title-text h2 { font-size: 19px; font-weight: 800; color: #fff; letter-spacing: -0.3px; }
.uni-modal-title-text p { font-size: 12px; color: rgba(255,255,255,0.75); margin-top: 3px; font-family: var(--mono); }
.uni-modal-close {
  width: 38px; height: 38px; border-radius: 10px;
  background: rgba(255,255,255,0.2); border: 1px solid rgba(255,255,255,0.3);
  color: #fff; font-size: 18px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s; flex-shrink: 0; line-height: 1;
}
.uni-modal-close:hover { background: rgba(255,255,255,0.35); transform: scale(1.08); }

.uni-summary-strip { display: flex; border-bottom: 1px solid var(--border2); }
.uni-summary-item {
  flex: 1; padding: 14px 16px;
  display: flex; flex-direction: column; align-items: center;
  border-right: 1px solid var(--border2); background: var(--card2);
}
.uni-summary-item:last-child { border-right: none; }
.uni-summary-value { font-size: 24px; font-weight: 800; font-family: var(--mono); letter-spacing: -1px; line-height: 1; }
.uni-summary-label { font-size: 11px; color: var(--text3); font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; font-family: var(--mono); margin-top: 4px; text-align: center; }

.uni-search-row { padding: 14px 20px; border-bottom: 1px solid var(--border); background: var(--card); }
.uni-search-wrap { position: relative; }
.uni-search-input {
  width: 100%; padding: 9px 14px 9px 36px;
  border: 1.5px solid var(--border2); border-radius: 9px;
  font-size: 13px; font-family: var(--font); color: var(--text);
  background: var(--card2); outline: none; transition: border 0.15s;
}
.uni-search-input:focus { border-color: var(--blue); }
.uni-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); font-size: 14px; pointer-events: none; }

.uni-cards-body {
  padding: 16px 20px 20px; max-height: 520px; overflow-y: auto; background: var(--bg);
}
.uni-cards-body::-webkit-scrollbar { width: 5px; }
.uni-cards-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

.uni-cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }

.uni-visitor-card {
  background: var(--card); border-radius: 13px; border: 1.5px solid var(--border2);
  overflow: hidden; transition: all 0.18s; box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.uni-visitor-card:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(0,0,0,0.10); }

.uni-card-top { padding: 14px 14px 12px; display: flex; align-items: flex-start; gap: 11px; border-bottom: 1px solid var(--border); }
.uni-card-avatar {
  width: 44px; height: 44px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px; font-weight: 800; color: #fff; text-transform: uppercase; flex-shrink: 0; font-family: var(--mono);
}
.uni-card-identity { flex: 1; min-width: 0; }
.uni-card-name { font-size: 14px; font-weight: 700; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.uni-card-id { font-size: 11px; color: var(--text3); font-family: var(--mono); margin-top: 2px; }
.uni-card-badge { font-size: 10px; font-weight: 800; padding: 4px 9px; border-radius: 6px; font-family: var(--mono); white-space: nowrap; flex-shrink: 0; letter-spacing: 0.04em; }

.uni-card-body { padding: 12px 14px; display: flex; flex-direction: column; gap: 7px; }
.uni-card-row { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--text2); }
.uni-card-row-icon { font-size: 13px; width: 18px; text-align: center; flex-shrink: 0; }
.uni-card-row-label { color: var(--text3); font-family: var(--mono); font-size: 10px; font-weight: 600; min-width: 58px; }
.uni-card-row-val   { font-weight: 600; color: var(--text); flex: 1; font-size: 12px; }

.uni-card-footer {
  padding: 10px 14px; background: var(--card2); border-top: 1px solid var(--border);
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.uni-card-office { font-size: 11px; color: var(--text3); font-family: var(--mono); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; }

.uni-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; gap: 10px;
}
.uni-empty-icon { font-size: 48px; opacity: 0.3; }
.uni-empty-text { font-size: 15px; font-weight: 700; color: var(--text2); }
.uni-empty-sub  { font-size: 12px; color: var(--text3); }

@media (max-width: 640px) {
  .dash-header { padding: 18px 20px; }
  .dash-body { padding: 20px 16px 0; }
  .uni-modal { border-radius: 14px; }
  .uni-cards-grid { grid-template-columns: 1fr; }
  .uni-summary-strip { flex-wrap: wrap; }
  .uni-summary-item { min-width: 50%; }
}
`;

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  "#2563eb","#059669","#d97706","#dc2626","#7c3aed",
  "#db2777","#0891b2","#65a30d","#ea580c","#9333ea",
];

const avatarColor = (i) => AVATAR_COLORS[i % AVATAR_COLORS.length];

const initials = (name = "") => {
  const p = name.trim().split(" ");
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : (p[0]?.[0] ?? "?").toUpperCase();
};

const getRank = (i) => ["🥇","🥈","🥉"][i] ?? `#${i + 1}`;

const OFFICE_LOCATIONS = {
  location1: "Downtown HQ",
  location2: "North Wing Office",
  location3: "Westside Branch",
};

const resolveLocation = (s) => {
  const raw = s.gym_location || s.boarding_type || "";
  return OFFICE_LOCATIONS[raw] || raw || "N/A";
};

/* ─── StatCard ─────────────────────────────────────────────────────────────── */
const StatCard = ({ icon, iconBg, label, value, sub, accent, onClick, clickHint }) => (
  <div className="dash-stat" style={{ "--accent": accent, "--icon-bg": iconBg }} onClick={onClick}>
    <div className="dash-stat-icon-wrap">{icon}</div>
    <div className="dash-stat-label">{label}</div>
    <div className="dash-stat-value">{value ?? "—"}</div>
    <div className="dash-stat-sub">{sub}</div>
    {clickHint && <div className="dash-stat-click-hint">👆 {clickHint}</div>}
  </div>
);

/* ─── VisitorList ────────────────────────────────────────────────────────── */
const VisitorList = ({ visitors = [], type = "present", loading }) => {
  if (loading) return (
    <div className="dash-spinner-row" style={{ padding: "18px 14px" }}>
      <div className="dash-spinner" /><span>Loading data…</span>
    </div>
  );

  if (!visitors.length) return (
    <div className="dash-empty">
      <div className="dash-empty-icon">{type === "present" ? "🚶" : "⭐"}</div>
      <div className="dash-empty-text">No data found</div>
    </div>
  );

  return (
    <div className="dash-visitor-list">
      {visitors.map((c, i) => {
        const name = c.member_name || c.name || c.member_id || `Visitor ${i + 1}`;
        const hrs  = parseFloat(c.total_workout_hours) || 0;
        
        let metaItems = [];

        if (type === "present") {
          if (c.check_in_time) metaItems.push(`🕐 ${c.check_in_time}`);
          if (c.gym_location || c.boarding_type) metaItems.push(`📍 ${resolveLocation(c)}`);
        } else if (type === "repetitive") {
          metaItems.push(`🔄 ${c.weekly_visits || 2} recognitions today`);
        } else if (type === "suspect") {
          metaItems.push(`🚨 Detected ${c.total_detection_logs || 1} times`);
        }

        return (
          <div key={i} className="dash-visitor-row">
            <div className="dash-rank">{getRank(i)}</div>
            <div className="dash-avatar" style={{ background: avatarColor(i) }}>{initials(name)}</div>
            <div className="dash-visitor-info">
              <div className="dash-visitor-name">{name}</div>
              <div className="dash-visitor-meta">
                {metaItems.join("  ")}
              </div>
            </div>
            <div className="dash-visitor-right">
              {hrs > 0 && type === "present" && <span className="dash-badge blue">{hrs} hrs</span>}
              {type === "present" && <span className="dash-badge green">In-Office</span>}
              {type === "repetitive" && <span className="dash-badge amber">Frequent</span>}
              {type === "suspect" && <span className="dash-badge red">Suspect</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   UNIVERSAL MODAL — used for Total Visitors, Present, Suspects, etc.
══════════════════════════════════════════════════════════════════════════════ */
const UniversalModal = ({ config, onClose }) => {
  const [search, setSearch] = useState("");
  const { title, subtitle, icon, headerGradient, visitors, summaryItems, renderCard } = config;

  const filtered = visitors.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name  = (c.member_name  || c.name  || "").toLowerCase();
    const id    = (c.member_id    || c.id    || "").toLowerCase();
    const phone = (c.phone_number || c.mobile_no || "").toLowerCase();
    return name.includes(q) || id.includes(q) || phone.includes(q);
  });

  return (
    <div className="uni-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="uni-modal">
        {/* Header */}
        <div className="uni-modal-header" style={{ background: headerGradient }}>
          <div className="uni-modal-title">
            <div className="uni-modal-title-icon">{icon}</div>
            <div className="uni-modal-title-text">
              <h2>{title}</h2>
              <p>{subtitle}</p>
            </div>
          </div>
          <button className="uni-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Summary strip */}
        {summaryItems && (
          <div className="uni-summary-strip">
            {summaryItems.map((s, i) => (
              <div key={i} className="uni-summary-item">
                <div className="uni-summary-value" style={{ color: s.color }}>{s.value}</div>
                <div className="uni-summary-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="uni-search-row">
          <div className="uni-search-wrap">
            <span className="uni-search-icon">🔍</span>
            <input className="uni-search-input" placeholder="Search by name, ID, or phone…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Cards */}
        <div className="uni-cards-body">
          {filtered.length === 0 ? (
            <div className="uni-empty">
              <div className="uni-empty-icon">🎉</div>
              <div className="uni-empty-text">{search ? "No matches found" : "No data found"}</div>
            </div>
          ) : (
            <div className="uni-cards-grid">
              {filtered.map((c, i) => renderCard(c, i))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const VisitorAnalyticsBoard = ({ selectedDate }) => {
  const [allVisitors,      setAllVisitors]      = useState([]);
  const [visitorsLoading,  setVisitorsLoading]  = useState(true);
  const [locations,        setLocations]        = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  const [sessionStatus,    setSessionStatus]    = useState(null);
  const [sessionLoading,   setSessionLoading]   = useState(false);
  const [suspectsSession,  setSuspectsSession]  = useState(null);

  const [locLoading,       setLocLoading]       = useState(false);
  const [openModal,        setOpenModal]        = useState(null);

  // ✅ STATE TO HOLD THE BACKEND STATS (Keys kept same to match backend payload)
  const [dashboardStats, setDashboardStats] = useState({
    total_customers: 0,
    walk_ins_today: 0,
    repetitive_customers: 0,
    avg_visit_hours: 0,
    total_suspects: 0,
    returning_suspects: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  /* ── Fetch all registered visitors (for Modal lists) ── */
  useEffect(() => {
    setVisitorsLoading(true);
    get_all_registered_students()
      .then(res => setAllVisitors(res.data || []))
      .catch(() => toast.error("Failed to load visitor data"))
      .finally(() => setVisitorsLoading(false));
  }, []);

  /* ── Fetch locations ── */
  useEffect(() => {
    if (!selectedDate) return;
    setLocLoading(true);
    setLocations([]); setSelectedLocation(null); setSessionStatus(null);
    getMemberLocationsByDateService({ date: selectedDate })
      .then(res => {
        const locs = res.data.locations || [];
        setLocations(locs);
        if (locs.length) setSelectedLocation(locs[0]);
      })
      .catch(() => { setLocations([]); setSelectedLocation(null); })
      .finally(() => setLocLoading(false));
  }, [selectedDate]);

  /* ── ✅ FETCH BACKEND DASHBOARD SUMMARY DATA (For the 6 Cards) ── */
  useEffect(() => {
    if (!selectedDate) return;
    setStatsLoading(true);

    const payload = {
        date: selectedDate,
        location: selectedLocation || undefined
    };

    get_home_page_summary(payload)
      .then(res => {
          if (res.data && res.data.length > 0) {
              setDashboardStats(res.data[0]);
          } else {
              setDashboardStats({
                  total_customers: 0,
                  walk_ins_today: 0,
                  repetitive_customers: 0,
                  avg_visit_hours: 0,
                  total_suspects: 0,
                  returning_suspects: 0
              });
          }
      })
      .catch(() => console.error("Failed to fetch dashboard stats"))
      .finally(() => setStatsLoading(false));

  }, [selectedDate, selectedLocation]);

/* ── COMBINED CONCURRENT FETCH FOR DASHBOARD DATA ── */
  useEffect(() => {
    if (!selectedLocation || !selectedDate) return;
    
    setStatsLoading(true);
    setSessionLoading(true);

    const payloadStats = { date: selectedDate, location: selectedLocation };
    const payloadSession = { location: selectedLocation, session_id: 0, date: selectedDate, include_images: false };
    const payloadSuspects = { date: selectedDate }; 

    Promise.allSettled([
      get_home_page_summary(payloadStats),
      getMemberSessionAttendanceService(payloadSession),
      get_suspect_report(payloadSuspects)
    ]).then(([statsRes, sessionRes, suspectsRes]) => {
      
      // 1. Resolve Stats
      if (statsRes.status === "fulfilled" && statsRes.value?.data?.length > 0) {
        setDashboardStats(statsRes.value.data[0]);
      } else {
        setDashboardStats({ total_customers: 0, walk_ins_today: 0, repetitive_customers: 0, avg_visit_hours: 0, total_suspects: 0, returning_suspects: 0 });
      }

      // 2. Resolve Session (Walk-ins)
      if (sessionRes.status === "fulfilled") {
        setSessionStatus(sessionRes.value.data);
      } else {
        setSessionStatus(null);
      }

      // 3. Resolve Suspects
      if (suspectsRes.status === "fulfilled") {
        setSuspectsSession(suspectsRes.value.data?.data || []);
      } else {
        setSuspectsSession([]);
      }

    }).finally(() => {
      setStatsLoading(false);
      setSessionLoading(false);
    });

  }, [selectedLocation, selectedDate]);

  /* ── Extraction helper to flatten data if session_id=0 returns nested array ── */
  const extractData = (statusObj) => {
    if (!statusObj) return [];
    let extracted = [];
    if (statusObj.sessions) {
        statusObj.sessions.forEach(s => {
            if (s.present?.data) extracted.push(...s.present.data);
        });
    } else if (statusObj.present?.data) {
        extracted = statusObj.present.data;
    } else if (Array.isArray(statusObj.data)) {
        extracted = statusObj.data;
    }
    return extracted;
  };

  /* ── COMPUTED VALUES FOR LISTS/MODALS ── */
  // Extract real Present Data
  const presentVisitors = useMemo(() => extractData(sessionStatus), [sessionStatus]);

  // Deriving "Repetitive Visitors" based on actual recognitions today
  const repetitiveVisitors = useMemo(() => {
    return presentVisitors
      .filter(c => {
          const logs = parseInt(c.total_detection_logs || c.total_detections || 0, 10);
          return logs >= 2;
      })
      .map(c => ({
        ...c,
        weekly_visits: parseInt(c.total_detection_logs || c.total_detections || 0, 10)
      }));
  }, [presentVisitors]);

  /* ── Actual Suspects Data extraction for Modal ── */
  const suspectsData = useMemo(() => {
    const unknowns = suspectsSession || []; 
    
    // Group detections by suspect_id to count returning occurrences
    const groupedSuspects = {};

    unknowns.forEach(u => {
        const s_id = u.suspect_id || "UNKNOWN";
        if (!groupedSuspects[s_id]) {
            groupedSuspects[s_id] = {
                id: u.id,
                member_id: u.suspect_id,
                member_name: u.suspect_name || "Unidentified Suspect",
                gym_location: u.location || selectedLocation,
                mobile_no: "N/A",
                status: "Suspect",
                check_in_time: u.detection_time, // Used for display in the card
                total_detection_logs: 0
            };
        }
        groupedSuspects[s_id].total_detection_logs += 1;
    });

    return Object.values(groupedSuspects);
  }, [suspectsSession, selectedLocation]);

  const topPerformers = [...presentVisitors]
    .map(c => ({ ...c, _hrs: parseFloat(c.total_workout_hours) || 0 }))
    .filter(c => c._hrs > 0)
    .sort((a,b) => b._hrs - a._hrs)
    .slice(0, 7);
  const maxHrsForBar = topPerformers.length ? topPerformers[0]._hrs : 1;

  /* ─ CARD RENDERERS ── */
  const renderBasicCard = (accentColor, badgeLabel) => (c, i) => {
    const name = c.member_name || c.name || c.member_id || `Visitor ${i+1}`;
    const locName = resolveLocation(c);

    return (
      <div key={i} className="uni-visitor-card">
        <div className="uni-card-top">
          <div className="uni-card-avatar" style={{ background: avatarColor(i) }}>
            {initials(name)}
          </div>
          <div className="uni-card-identity">
            <div className="uni-card-name">{name}</div>
            <div className="uni-card-id">ID: {c.member_id || c.id || "N/A"}</div>
          </div>
          <div className="uni-card-badge" style={{ background:`${accentColor}20`, color:accentColor, border:`1px solid ${accentColor}40` }}>
            {badgeLabel}
          </div>
        </div>

        <div className="uni-card-body">
          {(c.phone_number || c.mobile_no) && (
            <div className="uni-card-row">
              <span className="uni-card-row-icon">📞</span>
              <span className="uni-card-row-label">Phone</span>
              <span className="uni-card-row-val">{c.phone_number || c.mobile_no}</span>
            </div>
          )}

          {c.check_in_time && (
            <div className="uni-card-row">
              <span className="uni-card-row-icon">🕐</span>
              <span className="uni-card-row-label">Check In</span>
              <span className="uni-card-row-val">{c.check_in_time}</span>
            </div>
          )}
          
          {c.weekly_visits && badgeLabel !== "SUSPECT" && (
             <div className="uni-card-row">
               <span className="uni-card-row-icon">🔄</span>
               <span className="uni-card-row-label">Visits</span>
               <span className="uni-card-row-val">{c.weekly_visits} recognitions today</span>
             </div>
          )}
          
          {badgeLabel === "SUSPECT" && (
             <div className="uni-card-row">
               <span className="uni-card-row-icon">🚨</span>
               <span className="uni-card-row-label">Detections</span>
               <span className="uni-card-row-val" style={{ color: '#dc2626' }}>{c.total_detection_logs} times today</span>
             </div>
          )}
        </div>

        <div className="uni-card-footer">
          <div className="uni-card-office" title={locName}>📍 {locName}</div>
        </div>
      </div>
    );
  };

  /* ── MODAL CONFIGS ── */
  const MODAL_CONFIGS = {
    total: {
      title: "All Registered Visitors",
      subtitle: `Complete visitor directory — ${dashboardStats.total_customers} total`,
      icon: "👥",
      headerGradient: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
      visitors: allVisitors.map(s => ({
          id: s.id,
          member_id: s.student_id,
          member_name: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.student_id,
          phone_number: s.mobile_no,
          gym_location: s.boarding_type
      })),
      summaryItems: [
        { value: dashboardStats.total_customers, label: "Total Registered", color: "#334155" },
      ],
      renderCard: renderBasicCard("#334155", "VISITOR"),
    },
    present: {
      title: "Walk-ins Today",
      subtitle: `Visitors detected in-office — ${dashboardStats.walk_ins_today} today`,
      icon: "✅",
      headerGradient: "linear-gradient(135deg, #064e3b 0%, #059669 100%)",
      visitors: presentVisitors,
      summaryItems: [
        { value: dashboardStats.walk_ins_today,  label: "In Office",  color: "#059669" },
        { value: dashboardStats.avg_visit_hours, label: "Avg Stay",   color: "#0891b2" },
      ],
      renderCard: renderBasicCard("#059669", "PRESENT"),
    },
    repetitive: {
      title: "Repetitive Visitors",
      subtitle: `Visitors returning frequently — ${dashboardStats.repetitive_customers} this week`,
      icon: "⭐",
      headerGradient: "linear-gradient(135deg, #78350f 0%, #d97706 100%)",
      visitors: repetitiveVisitors,
      summaryItems: [
        { value: dashboardStats.repetitive_customers, label: "Frequent Visitors", color: "#d97706" },
      ],
      renderCard: renderBasicCard("#d97706", "FREQUENT"),
    },
    suspects: {
      title: "Detected Suspects Log",
      subtitle: `Total suspects and repeat offenders`,
      icon: "🚨",
      headerGradient: "linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)",
      visitors: suspectsData,
      summaryItems: [
        { value: dashboardStats.total_suspects,          label: "Total Suspects",  color: "#dc2626" },
        { value: dashboardStats.returning_suspects,      label: "Returned Usually",color: "#991b1b" },
      ],
      renderCard: renderBasicCard("#dc2626", "SUSPECT"),
    },
  };

  return (
    <>
      <style>{css}</style>
      <div className="dash-root">

        {/* HEADER */}
        <div className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <div className="dash-brand-icon">🏢</div>
              <div className="dash-brand-text">
                <h1>Office Visitor Dashboard</h1>
                <p>Visitor Footfall &amp; Insights</p>
              </div>
            </div>
            <div className="dash-date-pill">
              📅 {selectedDate
                ? new Date(selectedDate).toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long", year:"numeric" })
                : new Date().toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long", year:"numeric" })
              }
            </div>
          </div>
        </div>

        <div className="dash-body">

          {/* LOCATION FILTER */}
          <div className="dash-loc-bar">
            <span className="dash-loc-label">🏢 Branch</span>
            {locLoading ? (
              <div className="dash-spinner-row" style={{ padding: 0 }}>
                <div className="dash-spinner" /><span>Loading locations…</span>
              </div>
            ) : locations.length > 0 ? (
              <div className="dash-loc-pills">
                {locations.map(loc => (
                  <button key={loc} className={`dash-loc-pill${selectedLocation===loc?" active":""}`} onClick={() => setSelectedLocation(loc)}>
                    {loc}
                  </button>
                ))}
              </div>
            ) : (
              <span style={{ fontSize: 13, color: "var(--text3)", fontStyle: "italic" }}>
                No locations found for {selectedDate}
              </span>
            )}
          </div>

          {/* 6 STAT CARDS — ✅ NOW USING BACKEND DATA */}
          <div className="dash-section">
            <div className="dash-stats-grid">
              <StatCard icon="👥" iconBg="#f1f5f9" label="Registered Visitors" value={statsLoading ? "…" : dashboardStats.total_customers} sub="Total in Database" accent="#334155" onClick={statsLoading ? undefined : () => setOpenModal("total")} clickHint="View all visitors" />
              <StatCard icon="🚶" iconBg="#f0fdf4" label="Walk-ins Today" value={statsLoading ? "…" : dashboardStats.walk_ins_today} sub="Total Footfall" accent="#059669" onClick={statsLoading ? undefined : () => setOpenModal("present")} clickHint="View today's visitors" />
              <StatCard icon="⭐" iconBg="#fffbeb" label="Repetitive Visitors" value={statsLoading ? "…" : dashboardStats.repetitive_customers} sub="Recognized 2+ times" accent="#d97706" onClick={statsLoading ? undefined : () => setOpenModal("repetitive")} clickHint="View frequent visitors" />
              
              <StatCard icon="🚨" iconBg="#fef2f2" label="Total Suspects" value={statsLoading ? "…" : dashboardStats.total_suspects} sub="Detected anomalies" accent="#dc2626" onClick={statsLoading ? undefined : () => setOpenModal("suspects")} clickHint="View suspects log" />
              <StatCard icon="🔄" iconBg="#fef2f2" label="Returning Suspects" value={statsLoading ? "…" : dashboardStats.returning_suspects} sub="Detected 2+ times" accent="#991b1b" onClick={statsLoading ? undefined : () => setOpenModal("suspects")} clickHint="View suspects log" />
              <StatCard icon="⏳" iconBg="#ecfeff" label="Avg Office Visit" value={statsLoading ? "…" : dashboardStats.avg_visit_hours} sub="Hours per visitor" accent="#0891b2" />
            </div>
          </div>

          {/* PRESENT & REPETITIVE LISTS */}
          <div className="dash-section">
            <div className="dash-section-head">
              <h2>Visitor Activity — {selectedDate}</h2>
              <div className="dash-section-head-line" />
              <div className="dash-section-badge">{selectedLocation || "All Branches"}</div>
            </div>
            <div className="dash-lists-grid">
              <div className="dash-list-card">
                <div className="dash-list-head">
                  <div className="dash-list-head-icon green">🚶</div>
                  <div className="dash-list-head-title">Walk-ins Today</div>
                  <div className="dash-list-count green">{presentVisitors.length}</div>
                </div>
                <VisitorList visitors={presentVisitors} type="present" loading={sessionLoading} />
              </div>
              <div className="dash-list-card">
                <div className="dash-list-head">
                  <div className="dash-list-head-icon amber">⭐</div>
                  <div className="dash-list-head-title">Frequent Visitors</div>
                  <div className="dash-list-count amber">{repetitiveVisitors.length}</div>
                </div>
                <VisitorList visitors={repetitiveVisitors} type="repetitive" loading={sessionLoading} />
              </div>
            </div>
          </div>

          {/* TOP TIME PERFORMERS */}
          {topPerformers.length > 0 && (
            <div className="dash-section">
              <div className="dash-section-head">
                <h2>⏳ Longest Office Visits</h2>
                <div className="dash-section-head-line" />
                <div className="dash-section-badge">Today</div>
              </div>
              <div className="dash-visit-list">
                {topPerformers.map((c, i) => {
                  const pct  = maxHrsForBar > 0 ? (c._hrs / maxHrsForBar) * 100 : 0;
                  const name = c.member_name || c.member_id || `Visitor ${i + 1}`;
                  return (
                    <div key={i} className="dash-visit-row">
                      <div className="dash-visit-rank">{getRank(i)}</div>
                      <div className="dash-avatar" style={{ background: avatarColor(i), width: 36, height: 36, fontSize: 12 }}>
                        {initials(name)}
                      </div>
                      <div className="dash-visit-info">
                        <div className="dash-visit-name">{name}</div>
                        <div className="dash-visit-bar-bg">
                          <div className="dash-visit-bar" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <div className="dash-visit-hrs">{c._hrs} hrs</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── MODALS ── */}
      {openModal && MODAL_CONFIGS[openModal] && (
        <UniversalModal config={MODAL_CONFIGS[openModal]} onClose={() => setOpenModal(null)} />
      )}
    </>
  );
};

export default VisitorAnalyticsBoard;
